import { useRef, useState, useCallback, ChangeEvent, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Box, styled, keyframes } from '@mui/material';
import { ArrowUp } from 'lucide-react';
import { InputMask } from '@react-input/mask';
import { ChatInputRenderProps } from './chat-input';

const shimmer = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

interface Flyer {
  text: string;
  startLeft: number;
  startTop: number;
  startWidth: number;
  targetLeft: number;
  targetTop: number;
  targetWidth: number;
}

export const SimpleChatInput = ({
  input,
  setInput,
  handleSubmit,
  isTyping,
  placeholder,
  inputRef,
  mask,
  maskReplacement,
  maskTrack,
}: ChatInputRenderProps) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const [flyer, setFlyer] = useState<Flyer | null>(null);
  const [flyerFlying, setFlyerFlying] = useState(false);

  const onSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isTyping) return;

    const startRect = innerRef.current?.getBoundingClientRect();
    const chatEl = document.getElementById('chat-message-area');
    const chatRect = chatEl?.getBoundingClientRect();

    if (startRect && chatRect) {
      // measure text width using a hidden element
      const probe = document.createElement('span');
      probe.style.cssText = 'position:fixed;visibility:hidden;font-size:13px;padding:10px 12px;';
      probe.textContent = input;
      document.body.appendChild(probe);
      const bubbleWidth = Math.min(probe.offsetWidth, 240);
      document.body.removeChild(probe);

      setFlyer({
        text: input,
        startLeft: startRect.left,
        startTop: startRect.top,
        startWidth: startRect.width,
        targetLeft: chatRect.right - bubbleWidth - 16,
        targetTop: chatRect.bottom - 44,
        targetWidth: bubbleWidth,
      });
      setFlyerFlying(false);
      requestAnimationFrame(() => requestAnimationFrame(() => setFlyerFlying(true)));
      setTimeout(() => {
        setFlyer(null);
        setFlyerFlying(false);
        handleSubmit(e);
      }, 750);
    }

  }, [input, isTyping, handleSubmit]);

  return (
    <Wrap>
      <BorderShell>
        <GradientBg />
        <Shimmer />
        <Inner ref={innerRef}>
          {mask ? (
            <MaskedSearchInput
              ref={inputRef as any}
              mask={mask}
              replacement={maskReplacement}
              track={maskTrack}
              value={input}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') onSubmit(e as any); }}
              placeholder={placeholder}
              disabled={isTyping}
              autoFocus
            />
          ) : (
            <SearchInput
              ref={inputRef as any}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') onSubmit(e as any); }}
              placeholder={placeholder}
              disabled={isTyping}
              autoFocus
            />
          )}
          <SubmitBtn type="submit" aria-label="Send" disabled={isTyping || !input.trim()} onClick={onSubmit as any}>
            <ArrowUp size={16} color={'#374151'} />
          </SubmitBtn>
        </Inner>
      </BorderShell>

      {flyer && createPortal(
        <Box
          sx={{
            position: 'fixed',
            left:         flyerFlying ? flyer.targetLeft  : flyer.startLeft,
            top:          flyerFlying ? flyer.targetTop   : flyer.startTop,
            width:        flyerFlying ? flyer.targetWidth : flyer.startWidth,
            background:   flyerFlying ? '#2563eb'         : '#ffffff',
            color:        flyerFlying ? '#ffffff'         : '#374151',
            borderRadius: flyerFlying ? '16px 16px 2px 16px' : '999px',
            padding:      flyerFlying ? '10px 12px'       : '10px 20px',
            border:       flyerFlying ? '1px solid transparent' : '1px solid #e5e7eb',
            fontSize: 13,
            fontFamily: 'inherit',
            lineHeight: 1.5,
            pointerEvents: 'none',
            zIndex: 9999,
            boxSizing: 'border-box',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transition: 'all 700ms cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          {flyer.text}
        </Box>,
        document.body,
      )}
    </Wrap>
  );
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const Wrap = styled(Box)<{ component?: string }>({
  padding: '12px 0 8px',
  flexShrink: 0,
  width: '100%',
});

const BorderShell = styled(Box)({
  position: 'relative',
  padding: 2,
  borderRadius: 999,
  overflow: 'hidden',
  boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
});

const GradientBg = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to right, #60a5fa, #a855f7, #6366f1)',
  pointerEvents: 'none',
});

const Shimmer = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.85), transparent)',
  animation: `${shimmer} 5s linear infinite`,
  pointerEvents: 'none',
});

const Inner = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: '#ffffff',
  borderRadius: 999,
  paddingLeft: 20,
  paddingRight: 20,
  paddingTop: 12,
  paddingBottom: 12,
});

const SearchInput = styled('input')({
  flex: 1,
  minWidth: 0,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  fontSize: '0.875rem',
  color: '#374151',
  fontFamily: 'inherit',
  '&::placeholder': { color: '#9ca3af' },
  '&:disabled': { opacity: 0.6 },
});

const MaskedSearchInput = styled(InputMask)({
  flex: 1,
  minWidth: 0,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  fontSize: '0.875rem',
  color: '#374151',
  fontFamily: 'inherit',
  '&::placeholder': { color: '#9ca3af' },
  '&:disabled': { opacity: 0.6 },
});

const SubmitBtn = styled('button')({
  flexShrink: 0,
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: '#f3f4f6',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#374151',
  transition: 'background 150ms',
  '&:hover:not(:disabled)': { background: '#e5e7eb' },
  '&:disabled': { opacity: 0.4, cursor: 'default' },
});
